import React, { useState } from 'react'
import moment from 'moment'
import {
  makeStyles,
  TextField,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Edit'
import { useDispatch } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import { createItem, updateStock } from '../slice'

import { useHistory } from 'react-router-dom'

const useStyles = makeStyles(theme => ({
  table: {
    width: '100%',
  },
  itemName: {
    width: '25%',
  },
  itemUnit: {
    width: '10%',
  },
  dateColumn: {
    width: `calc( (85% - 25% - 10%) / 5 )`,
  },
  nowColumn: {
    width: '25%',
  },
  input: {
    width: '100%',
  },
  nowInput: {
    minWidth: '15%',
  },
  reasonInput: {
    width: '100%',
  },
  discardedText: {
    color: theme.palette.text.secondary,
  },
  flexRow: {
    display: 'flex',
    alignItems: 'center',
  },
  roundedRectangle: {
    margin: theme.spacing(2, 0),
    borderRadius: 15,
    boxShadow: '0px 3px 8px rgba(0, 0, 0, 0.12)',
    overflow: 'hidden',
  },
}))

const InventoryStockItemModifyForm = ({ location, items }) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()

  const [isModalOpen, setModalOpen] = useState(false)
  const [selectedUnit, setSelectedUnit] = useState('')
  const [newItem, setNewItem] = useState(null)
  const [stockChanges, setStockChanges] = useState({})

  const [newItemName, setNewItemName] = useState('')
  const [newItemUnit, setNewItemUnit] = useState('')
  const [newItemInStock, setNewItemInStock] = useState('')
  const [newItemDiscarded, setNewItemDiscarded] = useState('')
  const [newItemReasonForDiscarding, setNewItemReasonForDiscarding] =
    useState('')

  const handleInputChange = (event, itemId, key) => {
    const value = event.target.value
    setStockChanges(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [key]: value,
      },
    }))
  }

  const handleOpenModal = unit => {
    setSelectedUnit(unit)
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalOpen(false)
  }

  const handleSave = () => {
    setModalOpen(false)
  }

  const handleSubmit = async () => {
    // Process the new item first...
    if (newItem) {
      try {
        let result = await dispatch(
          createItem({
            environmentId: location.environment,
            locationId: location.id,
            body: {
              name: newItemName,
              unit: newItemUnit,
            },
          })
        )

        unwrapResult(result)
        let createdItem = result.payload

        if (newItemInStock || newItemDiscarded) {
          await dispatch(
            updateStock({
              environmentId: location.environment,
              locationId: location.id,
              itemId: createdItem.id,
              recordDate: new Date().toISOString().split('T')[0],
              body: {
                in_stock: newItemInStock,
                discarded: newItemDiscarded,
                reason_for_discarding: newItemReasonForDiscarding,
              },
            })
          )
        } else {
          console.log('no stock to update for new item')
        }
      } catch (error) {
        console.error('Failed to create item', error)
      }
    }

    // Now, process the updates from stockChanges...
    for (let itemId in stockChanges) {
      const change = stockChanges[itemId]
      if (change.inStock || change.discarded) {
        try {
          await dispatch(
            updateStock({
              environmentId: location.environment,
              locationId: location.id,
              itemId: itemId,
              recordDate: new Date().toISOString().split('T')[0],
              body: {
                in_stock: change.inStock,
                discarded: change.discarded,
                reason_for_discarding: change.reasonForDiscarding,
              },
            })
          )
        } catch (error) {
          console.error('Failed to update stock', error)
        }
      }
    }

    history.push('/inventory')
  }

  let allDates = []
  items.forEach(item => {
    item.stock.inStock.forEach(stock => allDates.push(stock.date))
    item.stock.discarded.forEach(discard => allDates.push(discard.date))
  })

  allDates = [...new Set(allDates)].sort((a, b) => new Date(b) - new Date(a))

  const latestDates = allDates.slice(0, 5)

  return (
    <TableContainer>
      <Table className={classes.table} aria-label="stock table">
        <TableHead>
          <TableRow>
            <TableCell className={classes.itemName}>Item</TableCell>
            <TableCell className={classes.itemUnit}>Unit</TableCell>
            {latestDates.map(date => (
              <TableCell key={date} className={classes.dateColumn}>
                {moment(date).format('Do MMM YY')}
              </TableCell>
            ))}
            <TableCell className={classes.nowColumn}>Now</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map(item => (
            <React.Fragment key={item.name}>
              <TableRow>
                <TableCell className={classes.itemName}>
                  <Typography variant="body1">{item.name}</Typography>
                </TableCell>
                <TableCell className={classes.itemUnit}>
                  <div className={classes.flexRow}>
                    <Typography variant="body1">{item.unit}</Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal(item.unit)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </div>
                </TableCell>
                {latestDates.map(date => (
                  <TableCell key={date} className={classes.dateColumn}>
                    <Typography variant="body1">
                      {item.stock.inStock.find(stock => stock.date === date)
                        ?.amount || '-'}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell className={classes.nowColumn}>
                  <TextField
                    placeholder="Enter amount"
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.input}
                    value={stockChanges[item.id]?.inStock || ''}
                    onChange={e => handleInputChange(e, item.id, 'inStock')}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.itemName}>
                  <Typography variant="body1" className={classes.discardedText}>
                    Discarded
                  </Typography>
                </TableCell>
                <TableCell className={classes.itemUnit}></TableCell>
                {latestDates.map(date => (
                  <TableCell key={date} className={classes.dateColumn}>
                    <Typography variant="body1">
                      {item.stock.discarded.find(
                        discard => discard.date === date
                      )?.amount || '-'}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell className={classes.nowColumn}>
                  <TextField
                    placeholder="Enter amount"
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.input}
                    value={stockChanges[item.id]?.discarded || ''}
                    onChange={e => handleInputChange(e, item.id, 'discarded')}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={3 + latestDates.length} align="right">
                  <TextField
                    placeholder="Reason for discarding"
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.reasonInput}
                    value={stockChanges[item.id]?.reasonForDiscarding || ''}
                    onChange={e =>
                      handleInputChange(e, item.id, 'reasonForDiscarding')
                    }
                  />
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}

          {newItem && (
            <React.Fragment>
              <TableRow>
                <TableCell className={classes.itemName}>
                  <TextField
                    placeholder="Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.input}
                    value={newItemName}
                    onChange={e => setNewItemName(e.target.value)}
                  />
                </TableCell>
                <TableCell className={classes.itemUnit}>
                  <TextField
                    placeholder="Unit"
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.input}
                    value={newItemUnit}
                    onChange={e => setNewItemUnit(e.target.value)}
                  />
                </TableCell>
                {latestDates.map((date, index) => (
                  <TableCell
                    key={index}
                    className={classes.dateColumn}
                  ></TableCell>
                ))}
                <TableCell className={classes.nowColumn}>
                  <TextField
                    placeholder="In Stock"
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.input}
                    value={newItemInStock}
                    onChange={e => setNewItemInStock(e.target.value)}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.itemName}>
                  <Typography variant="body1" className={classes.discardedText}>
                    Discarded
                  </Typography>
                </TableCell>
                <TableCell className={classes.itemUnit}></TableCell>
                {latestDates.map((date, index) => (
                  <TableCell
                    key={index}
                    className={classes.dateColumn}
                  ></TableCell>
                ))}
                <TableCell className={classes.nowColumn}>
                  <TextField
                    placeholder="Discarded"
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.input}
                    value={newItemDiscarded}
                    onChange={e => setNewItemDiscarded(e.target.value)}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={3 + latestDates.length} align="right">
                  <TextField
                    placeholder="Reason for discarding"
                    variant="outlined"
                    size="small"
                    fullWidth
                    className={classes.reasonInput}
                    value={newItemReasonForDiscarding}
                    onChange={e =>
                      setNewItemReasonForDiscarding(e.target.value)
                    }
                  />
                </TableCell>
              </TableRow>
            </React.Fragment>
          )}
        </TableBody>
      </Table>

      <div
        style={{
          marginTop: 16,
          marginRight: 18,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Button
          color="primary"
          variant="contained"
          onClick={() => setNewItem({})}
          style={{ marginRight: 16 }}
        >
          Add Item
        </Button>

        <Button color="primary" variant="contained" onClick={handleSubmit}>
          Save Changes
        </Button>
      </div>

      {/* The modal/dialog for editing unit */}
      <Dialog open={isModalOpen} onClose={handleCloseModal}>
        <DialogTitle>Change Unit</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            variant="outlined"
            value={selectedUnit}
            onChange={e => setSelectedUnit(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </TableContainer>
  )
}

export default InventoryStockItemModifyForm
